import React, { useCallback, useMemo } from 'react'
import ReactSelect, {
  DropdownIndicatorProps,
  GroupBase,
  OptionProps,
  Props as ReactSelectProps,
  components as ReactSelectComponents,
  MultiValue,
  ContainerProps,
  SingleValueProps,
  ClearIndicatorProps,
} from 'react-select'

import styled, { CSSObject } from '@emotion/styled'
import CreatableSelect from 'react-select/creatable'

import { useHandler } from '@surglogs-app/hooks'
import { Cross } from '@surglogs-app/icons'
import { WrapInToolTip } from '@surglogs-app/ui.tooltip'

import { textEllipsisProps } from '../../../mixins'
import { DropdownIcon } from '../DropdownIcon'
import { FlexBox } from '../FlexBox'
import { Icon } from '../Icon'
import { Text, TextBold } from '../Text'

import { CreatedSelectOption } from './SelectOption'
import { SelectOptionWithSubOptions } from './SelectOptionWithSubOptions'
import { SelectBaseOption } from './types'
import { useCustomStyles } from './useCustomStyles'

const SelectWrapper = styled(FlexBox)`
  flex: 1;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  font-family: ${({ theme }) => theme.ui.typography.fontFamilies.default};
`

export function isMultiValue<T>(value: unknown): value is MultiValue<T> {
  return Array.isArray(value)
}

const SingleValue = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  children,
  selectProps,
  ...props
}: SingleValueProps<Option, IsMulti, Group>) => {
  const { valuePrefix } = selectProps as SelectProps<Option, IsMulti, Group>

  const innerProps = useMemo(
    () => ({
      ...props.innerProps,
      role: 'option',
      'aria-label': 'selected-option',
    }),
    [props],
  )

  return (
    <ReactSelectComponents.SingleValue
      {...{ ...props, selectProps, innerProps }}>
      {valuePrefix != null ? (
        <Text {...{ color: 'inherit' }}>
          {valuePrefix}{' '}
          <TextBold {...{ color: 'inherit' }}>{children}</TextBold>
        </Text>
      ) : (
        children
      )}
    </ReactSelectComponents.SingleValue>
  )
}

const SelectContainer = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: ContainerProps<Option, IsMulti, Group>,
) => {
  const innerProps = useMemo(
    () => ({
      ...props.innerProps,
      role: 'listbox',
      'aria-label': props.selectProps?.['aria-label'],
      'aria-disabled': props.selectProps?.isDisabled,
    }),
    [props],
  )

  return <ReactSelectComponents.SelectContainer {...{ ...props, innerProps }} />
}

const DropdownIndicator = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: DropdownIndicatorProps<Option, IsMulti, Group>,
) => {
  return (
    <DropdownIcon
      {...{ width: 12, height: 24, isExpanded: props.selectProps.menuIsOpen }}
    />
  )
}

const ClearIndicator = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: ClearIndicatorProps<Option, IsMulti, Group>,
) => {
  return (
    <Icon
      {...{
        ...props.innerProps,
        size: 'abs.20',
        mr: 'xs',
        icon: Cross,
        role: 'button',
        'aria-label': 'Clear select',
        color: 'inherit',
      }}
    />
  )
}

export interface SelectProps<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> extends Omit<ReactSelectProps<Option, IsMulti, Group>, 'isDisabled'> {
  disabled?: boolean
  warning?: boolean
  invalid?: boolean
  creatable?: boolean
  menuInPortal?: boolean
  createOptionLabel?: string
  valuePrefix?: string
  menuMaxHeight?: CSSObject['maxHeight']
}

export const Select = <
  Option extends SelectBaseOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  isClearable = false,
  disabled = false,
  isMulti = false as IsMulti,
  menuInPortal,
  creatable,
  options,
  components: componentsProp,
  value,
  createOptionLabel = 'Create new option',
  'aria-label': ariaLabel,
  onChange,
  menuMaxHeight,
  ...rest
}: SelectProps<Option, IsMulti, Group>) => {
  const onRemoveClick = useHandler((val: Option) => {
    onChange?.(value?.filter((v: Option) => v.value !== val.value) ?? [], {
      action: 'remove-value',
      removedValue: val,
    })
  })

  const hasSubOptions = useMemo(() => {
    if (!Array.isArray(options)) {
      return false
    }

    return options?.find(
      (item) => item.subOptions && item.subOptions?.length > 0,
    )
  }, [options])

  const styles = useCustomStyles<Option, IsMulti, Group>({
    hasSubOptions,
    menuInPortal,
    menuMaxHeight,
  })

  const components = useMemo(
    () => ({
      SelectContainer,
      ClearIndicator,
      DropdownIndicator,
      SingleValue,
      Option: (props: OptionProps<Option, IsMulti, Group>) => {
        if (hasSubOptions) {
          return (
            <SelectOptionWithSubOptions
              {...{
                ...props,
                withParentOnClick: false,
                data: {
                  name: props.data.label,
                  onSubOptionClick: props.data.onSubOptionClick,
                  subOptions: props.data.subOptions?.map((i) => i.label),
                  subOptionsLabel: props.data.subOptionsLabel,
                  subOptionsDefaultValue: props.data.subOptionsDefaultValue,
                },
              }}
            />
          )
        }

        return <ReactSelectComponents.Option {...props} />
      },
      ...componentsProp,
    }),
    [componentsProp, hasSubOptions],
  )

  const formatCreateLabel = useCallback(
    (val: string) => {
      return <CreatedSelectOption {...{ value: val, createOptionLabel }} />
    },
    [createOptionLabel],
  )

  const props: ReactSelectProps<Option, IsMulti, Group> = {
    ...rest,
    isDisabled: disabled,
    options,
    styles,
    components,
    isClearable,
    controlShouldRenderValue: !isMulti,
    value,
    isMulti,
    onChange,
    'aria-label': ariaLabel,
    'aria-invalid': rest.invalid,
    ...(menuInPortal
      ? { menuPortalTarget: document.body, menuPosition: 'absolute' }
      : {}),
  }

  return (
    <SelectWrapper
      {...{ gap: 'sm', fd: 'column', h: isMulti ? 'auto' : 'xxl' }}>
      {creatable ? (
        <CreatableSelect {...{ ...props, formatCreateLabel }} />
      ) : (
        <ReactSelect {...props} />
      )}
      {isMulti && value && isMultiValue(value) && value?.length > 0 ? (
        <FlexBox {...{ gap: 'sm', wrap: 'wrap', w: '100%' }}>
          {value.map((item) => {
            return (
              <WrapInToolTip
                key={item.value}
                {...{
                  tooltipText: item.tooltipText,
                  hidden: !item.tooltipText,
                }}>
                <FlexBox
                  {...{
                    bra: 'xs',
                    h: 'xl',
                    bgc: 'navy.10',
                    miw: '0',
                    ai: 'center',
                    px: 'abs.12',
                    gap: 'sm',
                  }}>
                  <Text {...{ size: 'xxs', fw: 'bold', ...textEllipsisProps }}>
                    {item.label}
                  </Text>
                  <Icon
                    {...{
                      icon: Cross,
                      size: 'md',
                      color: 'red.100',
                      cursor: 'pointer',
                      onClick: () => onRemoveClick(item),
                    }}
                  />
                </FlexBox>
              </WrapInToolTip>
            )
          })}
        </FlexBox>
      ) : null}
    </SelectWrapper>
  )
}
